import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc, 
  addDoc, 
  serverTimestamp, 
  getDocs 
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { logHelpfulError } from '../utils/firebaseIndexHelper.js';

/**
 * Notification Service - Chunk 3 Implementation
 * Handles real-time notifications for task automation and smart actions
 */

class NotificationService {
  constructor(db) {
    this.db = db;
    this.activeListeners = new Map();
    this.notificationCallbacks = new Map();
    this.writePermissionCache = new Map(); // Cache write permission status
  }

  /**
   * Check if we have write permissions for a collection
   */
  async checkWritePermission(collectionName) {
    // Check cache first
    const cacheKey = collectionName;
    if (this.writePermissionCache.has(cacheKey)) {
      return this.writePermissionCache.get(cacheKey);
    }

    try {
      // Try a simple write operation to test permissions
      const testRef = collection(this.db, collectionName);
      await addDoc(testRef, {
        test: true,
        timestamp: serverTimestamp()
      });
      
      // If successful, cache the result
      this.writePermissionCache.set(cacheKey, true);
      return true;
    } catch (error) {
      // If failed, cache the result and return false
      this.writePermissionCache.set(cacheKey, false);
      return false;
    }
  }

  /**
   * Safe write operation with permission checking
   */
  async safeAddDoc(collectionRef, data) {
    const collectionName = collectionRef.path.split('/')[0];
    
    // Check if we have write permissions
    const hasPermission = await this.checkWritePermission(collectionName);
    if (!hasPermission) {
      console.group('🔒 Write Permission Notice');
      console.warn(`Skipping write to ${collectionName} - insufficient permissions`);
      console.info('This is expected behavior for certain user roles.');
      console.groupEnd();
      return null;
    }

    try {
      return await addDoc(collectionRef, data);
    } catch (error) {
      // Cache permission failure for future calls
      this.writePermissionCache.set(collectionName, false);
      logHelpfulError(error, `Notification Service - safeAddDoc (${collectionName})`);
      return null;
    }
  }

  /**
   * Start listening for notifications for a specific user/role
   */
  startNotificationListener(userId, userRole, orgId, callback) {
    const listenerId = `${userId}_${userRole}_${orgId}`;
    
    if (this.activeListeners.has(listenerId)) {
      console.log(`Notification listener already active for ${listenerId}`);
      return;
    }

    console.log(`Starting notification listener for ${listenerId}`);

    const notificationsRef = collection(this.db, 'c3_notifications');
    const q = query(
      notificationsRef,
      where('orgId', '==', orgId),
      where('targetRole', 'in', [userRole, 'all']),
      where('read', '==', false),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter for recent notifications (last 24 hours)
      const now = new Date();
      const twentyFourHoursAgo = new Date(now - 24 * 60 * 60 * 1000);
      
      const recentNotifications = notifications.filter(notification => {
        const createdAt = notification.createdAt?.toDate() || new Date(0);
        return createdAt >= twentyFourHoursAgo;
      });

      // Call the callback with notifications
      if (callback) {
        callback(recentNotifications);
      }

      // Store callback for later use
      this.notificationCallbacks.set(listenerId, callback);

      // Show toast notifications for urgent items
      this.showToastNotifications(recentNotifications);
    });

    this.activeListeners.set(listenerId, unsubscribe);
  }

  /**
   * Stop notification listener
   */
  stopNotificationListener(userId, userRole, orgId) {
    const listenerId = `${userId}_${userRole}_${orgId}`;
    const unsubscribe = this.activeListeners.get(listenerId);
    
    if (unsubscribe) {
      unsubscribe();
      this.activeListeners.delete(listenerId);
      this.notificationCallbacks.delete(listenerId);
      console.log(`Stopped notification listener for ${listenerId}`);
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId) {
    try {
      const notificationRef = doc(this.db, 'c3_notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
        readAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  /**
   * Mark all notifications as read for a user
   */
  async markAllAsRead(userRole, orgId) {
    try {
      const notificationsRef = collection(this.db, 'c3_notifications');
      const q = query(
        notificationsRef,
        where('orgId', '==', orgId),
        where('targetRole', '==', userRole),
        where('read', '==', false)
      );

      const snapshot = await getDocs(q);
      const updatePromises = snapshot.docs.map(doc => 
        updateDoc(doc.ref, {
          read: true,
          readAt: serverTimestamp()
        })
      );

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }

  /**
   * Create a custom notification
   */
  async createNotification(notification) {
    try {
      const notificationsRef = collection(this.db, 'c3_notifications');
      await this.safeAddDoc(notificationsRef, {
        ...notification,
        createdAt: serverTimestamp(),
        read: false
      });
    } catch (error) {
      logHelpfulError(error, 'Notification Service - createNotification');
      // Continue gracefully if notification creation fails
    }
  }

  /**
   * Show toast notifications for urgent items
   */
  showToastNotifications(notifications) {
    notifications.forEach(notification => {
      // Only show toast for urgent notifications that haven't been shown
      if (notification.priority === 'urgent' && !notification.toastShown) {
        const toastOptions = {
          position: "top-right",
          autoClose: 10000, // 10 seconds for urgent
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        };

        switch (notification.type) {
          case 'task_escalation':
            toast.error(`🚨 ${notification.title}: ${notification.message}`, toastOptions);
            break;
          case 'task_alert':
            toast.warning(`⏰ ${notification.title}: ${notification.message}`, toastOptions);
            break;
          case 'missing_tasks':
            toast.info(`📋 ${notification.title}: ${notification.message}`, toastOptions);
            break;
          case 'communication_gap':
            toast.warning(`📢 ${notification.title}: ${notification.message}`, toastOptions);
            break;
          case 'workload_imbalance':
            toast.info(`⚖️ ${notification.title}: ${notification.message}`, toastOptions);
            break;
          default:
            toast.info(`🔔 ${notification.title}: ${notification.message}`, toastOptions);
        }

        // Mark as toast shown to prevent duplicate toasts
        this.markToastShown(notification.id);
      }
    });
  }

  /**
   * Mark notification toast as shown
   */
  async markToastShown(notificationId) {
    try {
      const notificationRef = doc(this.db, 'c3_notifications', notificationId);
      await updateDoc(notificationRef, {
        toastShown: true
      });
    } catch (error) {
      console.error('Error marking toast as shown:', error);
    }
  }

  /**
   * Get notification statistics
   */
  async getNotificationStats(userRole, orgId) {
    try {
      const notificationsRef = collection(this.db, 'c3_notifications');
      const q = query(
        notificationsRef,
        where('orgId', '==', orgId),
        where('targetRole', '==', userRole)
      );

      const snapshot = await getDocs(q);
      const notifications = snapshot.docs.map(doc => doc.data());

      const stats = {
        total: notifications.length,
        unread: notifications.filter(n => !n.read).length,
        urgent: notifications.filter(n => n.priority === 'urgent' && !n.read).length,
        actionRequired: notifications.filter(n => n.actionRequired && !n.read).length,
        byType: {}
      };

      // Count by type
      notifications.forEach(notification => {
        const type = notification.type || 'other';
        if (!stats.byType[type]) {
          stats.byType[type] = { total: 0, unread: 0 };
        }
        stats.byType[type].total++;
        if (!notification.read) {
          stats.byType[type].unread++;
        }
      });

      return stats;
    } catch (error) {
      console.error('Error getting notification stats:', error);
      return {
        total: 0,
        unread: 0,
        urgent: 0,
        actionRequired: 0,
        byType: {}
      };
    }
  }

  /**
   * Clean up old notifications (older than 7 days)
   */
  async cleanupOldNotifications(orgId) {
    try {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const notificationsRef = collection(this.db, 'c3_notifications');
      const q = query(
        notificationsRef,
        where('orgId', '==', orgId),
        where('createdAt', '<', sevenDaysAgo)
      );

      const snapshot = await getDocs(q);
      const deletePromises = snapshot.docs.map(doc => doc.ref.delete());
      
      await Promise.all(deletePromises);
      console.log(`Cleaned up ${snapshot.docs.length} old notifications`);
    } catch (error) {
      console.error('Error cleaning up old notifications:', error);
    }
  }

  /**
   * Get notifications for incident
   */
  async getIncidentNotifications(incidentId, orgId) {
    try {
      const notificationsRef = collection(this.db, 'c3_notifications');
      const q = query(
        notificationsRef,
        where('incidentId', '==', incidentId),
        where('orgId', '==', orgId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting incident notifications:', error);
      return [];
    }
  }

  /**
   * Create bulk notifications for team
   */
  async createBulkNotifications(notifications) {
    try {
      const notificationsRef = collection(this.db, 'c3_notifications');
      const promises = notifications.map(notification => 
        this.safeAddDoc(notificationsRef, {
          ...notification,
          createdAt: serverTimestamp(),
          read: false
        })
      );

      await Promise.all(promises);
      console.log(`Created ${notifications.length} bulk notifications`);
    } catch (error) {
      console.error('Error creating bulk notifications:', error);
    }
  }

  /**
   * Get notification templates for common scenarios
   */
  getNotificationTemplates() {
    return {
      taskEscalation: {
        type: 'task_escalation',
        priority: 'urgent',
        title: 'URGENT: Task Escalation Required',
        actionRequired: true,
        suggestedActions: [
          'Reassign to available team member',
          'Extend deadline if appropriate',
          'Mark as completed if done',
          'Escalate to senior management'
        ]
      },
      taskAlert: {
        type: 'task_alert',
        priority: 'normal',
        title: 'Task Reminder',
        actionRequired: false
      },
      workloadImbalance: {
        type: 'workload_imbalance',
        priority: 'normal',
        title: 'Workload Imbalance Detected',
        actionRequired: true
      },
      missingTasks: {
        type: 'missing_tasks',
        priority: 'important',
        title: 'Missing Critical Tasks Detected',
        actionRequired: true,
        suggestedActions: ['Create missing tasks', 'Review task list', 'Confirm tasks not needed']
      },
      communicationGap: {
        type: 'communication_gap',
        priority: 'important',
        title: 'Communication Gap Detected',
        actionRequired: true,
        suggestedActions: [
          'Send stakeholder update',
          'Prepare media statement',
          'Update internal communications',
          'Schedule next communication'
        ]
      }
    };
  }
}

export default NotificationService; 